import type { SearchEntry } from "../../redux/features/savedSearchesSlice";
import {
  normalizeSavedSearchUrlInput,
  shouldSkipPersistingSavedSearchUrl,
} from "./normalize-saved-search-url";
import { savedSearchAlertId } from "./saved-search-alert-id";
import type { ServerSavedSearch } from "./server-saved-searches";

/** Mirrors MAX_SAVED_SEARCHES in redux/features/savedSearchesSlice.ts. */
const MAX_SAVED_SEARCHES = 50;

/** Canonical stored form: path + search, origin stripped (same basis as savedSearchAlertId). */
export function canonicalSavedSearchPath(storedUrl: string): string {
  return toPathAndSearch(storedUrl);
}

function toPathAndSearch(storedUrl: string): string {
  const normalized = normalizeSavedSearchUrlInput(storedUrl) || storedUrl.trim();
  try {
    const parsed = new URL(normalized, "https://www.jobcrawls.com");
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return normalized;
  }
}

export interface SavedSearchUpload {
  id: string;
  url: string;
  timestamp: number;
}

export interface SavedSearchSyncPlan {
  /** Local searches with no server item — to PUT (version 0). */
  uploads: SavedSearchUpload[];
  /** Union of local + server for setSavedSearches, newest first, capped. */
  mergedLocal: SearchEntry[];
  /** mergedLocal differs from the local list — dispatch only then. */
  changed: boolean;
}

/**
 * One-shot account sync for saved searches (spec §8): upload what the account
 * is missing, pull what other devices saved. Deletions are not reconciled here
 * (no tombstones) — the management UI deletes both sides directly.
 */
export function computeSavedSearchSyncPlan(
  local: SearchEntry[],
  server: ServerSavedSearch[]
): SavedSearchSyncPlan {
  const serverIds = new Set(server.map((s) => s.id));

  const normalizedLocal: SearchEntry[] = [];
  const seenLocalIds = new Set<string>();
  for (const entry of local) {
    if (typeof entry?.url !== "string") continue;
    const normalizedUrl = toPathAndSearch(entry.url);
    if (shouldSkipPersistingSavedSearchUrl(normalizedUrl)) continue;
    const id = savedSearchAlertId(normalizedUrl);
    if (seenLocalIds.has(id)) continue;
    seenLocalIds.add(id);
    normalizedLocal.push({ url: normalizedUrl, timestamp: entry.timestamp });
  }

  const uploads: SavedSearchUpload[] = normalizedLocal
    .filter((entry) => !serverIds.has(savedSearchAlertId(entry.url)))
    .map((entry) => ({ id: savedSearchAlertId(entry.url), url: entry.url, timestamp: entry.timestamp }));

  const pulls: SearchEntry[] = server
    .filter((s) => !seenLocalIds.has(s.id) && !shouldSkipPersistingSavedSearchUrl(s.url))
    .map((s) => ({
      url: toPathAndSearch(s.url),
      timestamp: typeof s.createdAt === "number" ? s.createdAt : 0,
    }));

  const mergedLocal = [...normalizedLocal, ...pulls]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_SAVED_SEARCHES);

  const changed =
    mergedLocal.length !== local.length ||
    mergedLocal.some((entry, index) => local[index]?.url !== entry.url);

  return { uploads, mergedLocal, changed };
}
